import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import { Button } from 'react-bootstrap';
import { Map } from 'immutable';
import styles from './style.scss';
import { trackEventView } from '../../utils/journey-tracking';

const propTypes = {
  settings: PropTypes.instanceOf(Map).isRequired,
  event: PropTypes.instanceOf(Map).isRequired,
  className: PropTypes.string,
};

function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const getUrl = (event, eventDataInUrl, settings) => {
  const url = event.get('url');
  if (validateEmail(url)) return `mailto:${url}`;
  if (eventDataInUrl) {
    const urlObj = new URL(url);
    const startTime = event.getIn(['date', 'start', 'utc']);
    const endTime = event.getIn(['date', 'end', 'utc']);
    const timezone = event.get('timezone');
    const momentStartTime = moment(startTime).tz(timezone);
    const momentEndTime = moment(endTime).tz(timezone);
    urlObj.searchParams.append('title', event.get('title'));
    urlObj.searchParams.append('date_start', momentStartTime.format());
    urlObj.searchParams.append('time_start_display', momentStartTime.format(settings.get('timeFormat')));
    urlObj.searchParams.append('time_end_display', momentEndTime.format(settings.get('timeFormat')));
    urlObj.searchParams.append('date_start_display', momentStartTime.format(settings.get('dateFormat')));
    urlObj.searchParams.append('date_end_display', momentEndTime.format(settings.get('dateFormat')));
    urlObj.searchParams.append('date_end', momentEndTime.format());
    urlObj.searchParams.append('timezone', event.get('timezone'));
    urlObj.searchParams.append('address', event.getIn(['venue', 'multi_line_address_display']));
    return urlObj.href;
  }
  return url;
};

const nonTicketEmbeddableEvent = (event) => event.get('type') !== 'google' && event.get('type') !== 'zoom';
function AttendButton({ settings, event, className = '', handleButtonClick }) {
  const eventBtnText = event.get('_design_event_attendee_button_text');
  let currentText = '';

  if (eventBtnText?.trim()) {
    // Use the event attendee button text if it's defined and not empty
    currentText = eventBtnText;
  } else {
    // Determine fallback button text
    const attendBtnText = settings.get('attendBtnText');
    const multiEventSelection = event.get('is_series_parent') && settings.get('multiDayDateSelector');
    const selectMultiEventDatesText = settings.get('selectMultiEventDatesText');

    // Use multi-event text if applicable, otherwise fallback to the attend button text
    currentText = multiEventSelection ? selectMultiEventDatesText : attendBtnText;
  }

  const pastText = settings.get('pastAttendBtnText');

  const eventDataInUrl = event.get('include_event_data_url');

  if (event.get('exceeded_quota') || event.get('event_setup') === 'nothing') {
    return <span />;
  }
  const text = event.get('is_past_event') ? pastText : currentText;
  const location = event.get('venue', '');
  const ticketSpotEnabled = event.get('type') === 'eventviewer' && event.get('use_checkout_form') && !event.get('max_attendee_limit_reached', false);
  const showCheckout =
    ticketSpotEnabled || (event.get('type') !== 'eventviewer' && settings.get('embedTicketPurchasing') && nonTicketEmbeddableEvent(event));
  if (event.get('type') === 'google' && location.startsWith && location.startsWith('https://zoom')) {
    return (
      <a href={location} className={cn('attendee_button', className)}>
        <Button
          onClick={handleButtonClick}
          className={cn({
            attendee_button: true,
            [styles.squareButtons]: !settings.get('buttonRounded'),
          })}
        >
          {settings.get('autoTranslateEvent') ? <FormattedMessage id="attend-button" defaultMessage="Register" /> : text}
        </Button>
      </a>
    );
  }
  if (event.get('type') === 'google' && location.startsWith && !location.startsWith('https://zoom')) {
    return <span />;
  }

  return showCheckout ? (
    <div className={cn('attendee_button', className)}>
      <TicketButton
        text={text}
        event={event}
        showCheckout={showCheckout}
        settings={settings}
        handleButtonClick={handleButtonClick}
        className={className}
        currentText={currentText}
      />
    </div>
  ) : (
    <a href={getUrl(event, eventDataInUrl, settings)} rel="noopener noreferrer" className={cn('attendee_button', className)} target="_blank">
      <TicketButton
        text={text}
        event={event}
        showCheckout={showCheckout}
        settings={settings}
        handleButtonClick={handleButtonClick}
        className={className}
        currentText={currentText}
      />
    </a>
  );
}

function TicketButton({ text, settings, showCheckout, className = '', handleButtonClick, currentText, event }) {
  const handleClick = (e) => {
    if (showCheckout && handleButtonClick) {
      // Track button click
      if (window.gtag) {
        window.gtag('event', 'click', {
          event_category: 'Ticket',
          event_label: currentText,
        });
      }
      if (window.fbq) {
        window.fbq('track', 'ViewContent');
      }

      // Track journey action
      trackEventView(event);

      handleButtonClick(e);
    }
  };

  return (
    <Button
      onClick={handleClick}
      className={cn({
        [className]: true,
        attendee_button: true,
        [styles.squareButtons]: !settings.get('buttonRounded'),
        [styles.attendButton]: true,
      })}
      data-ga-category="Ticket"
      data-ga-action="click"
      data-ga-label={currentText}
    >
      {settings.get('autoTranslateEvent') ? <FormattedMessage id="attend-button" defaultMessage="Register" /> : text}
    </Button>
  );
}

AttendButton.propTypes = propTypes;

export default AttendButton;
